/**
 * CostBreakdownTab
 *
 * The visible payoff of the cost-monitor work: every dollar this platform
 * spends, broken down four ways. Driven by llm_call_events ledger via the
 * /insights/cost/* endpoints.
 *
 *  1. KPI cards row — Total Cost, Cost/Conversation, Cache Savings %, Reasoning %
 *  2. Cost composition pie — input/output/cache_read/cache_write of total
 *  3. By call kind — chat vs embedding vs title vs summary vs ...
 *  4. By tool — which ADK tool burns the most (when chatbot_id is set)
 *  5. Provider rollup — invoice-shape, compare to credit card line by line
 */

import { useEffect, useMemo, useState } from 'react';
import type { TFunc } from '@/utils/tfunc';
import {
  Card,
  Col,
  Empty,
  Row,
  Space,
  Spin,
  Statistic,
  Select,
  Table,
  Tag,
  Tooltip as AntdTooltip,
  Typography,
  theme,
  message as antdMessage,
} from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import insightsService, {
  ApiKeyCatalogueEntry,
  ApiKeyCostRow,
  CallKindRow,
  CostByComponent,
  DailyTimeseriesRow,
  PerConversationAveragesResponse,
  ProviderRollupRow,
  TenantCostRow,
  ToolRow,
  ConversationCostPoint,
  ArtifactFormatRow,
} from '../services/insightsService';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { ConversationCostTimeline } from './ConversationCostTimeline';
import { TokenCompositionBar } from './TokenCompositionBar';
import { TypicalConversationCard } from './TypicalConversationCard';
import type { InsightFilters } from '../types/insights';
import { datePattern } from '@/utils/locale';

const { Text } = Typography;

interface CostBreakdownTabProps {
  filters: InsightFilters;
  /** Notifies the parent when a refetch starts/finishes, so the dashboard
   *  can render the "Refreshing…" indicator inline in the filter row up top
   *  instead of having a separate pill inside this tab. */
  onLoadingChange?: (loading: boolean) => void;
}

function fmtUsd(n: number): string {
  if (n == null || isNaN(n)) return '$0.000000';
  // Never round cost values — always show 6 decimals (the precision the
  // bill is computed at). Thousands separators on the integer side for
  // readability of large totals; full decimals on the fractional side.
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const [whole, decimals] = abs.toFixed(6).split('.');
  return `${sign}$${Number(whole).toLocaleString('en-US')}.${decimals}`;
}

function fmtN(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('en-US');
}

/**
 * Cost-monitor launched 2026-05-28. Before that date, the platform tracked
 * tokens only for the main chat call; embeddings, RAG rerank, web search,
 * background insight extraction, file enrichment were silent bill leaks.
 * That means pre-2026-05-28 cost numbers in the OLD Dashboard tab are an
 * undercount of the actual bill. The new Cost Breakdown tab only shows
 * post-cutover data — which is why the banner clarifies this.
 */
// CostMonitorCutoverBanner deleted — removed from UI per UX request.
// If we need a similar historical-data banner later, the cutover date
// was 2026-05-28; reintroduce as a small component then.


/**
 * Human-readable explanation of what each ADK tool actually does.
 * Shown next to the tool name in the By-Tool table so a user reading the
 * Insights page doesn't have to guess what "knowledge_search" means.
 */
const toolDescriptions = (t: TFunc): Record<string, { label: string; desc: string }> => ({
  knowledge_search: {
    label: t('insights.ragRetrieval'),
    desc: 'Embeds the user\'s query with text-embedding-3-small, semantic+lexical search against file chunks, and (when relevance is borderline) a gpt-5.4-mini rerank pass. The events you see under this tool are the embedding calls; rerank cost (if any) is logged separately under call_kind=rag_rerank.',
  },
  read_file: {
    label: t('insights.fileReader'),
    desc: 'Reads a specific attached file\'s full content into the next chat iteration\'s context. No LLM cost on the tool itself; the chat call that follows is bigger because the file is in its prompt.',
  },
  web_search: {
    label: t('costSimulator.agent.webSearch'),
    desc: 'Real-time web search via Gemini\'s grounding API. Recorded as call_kind=web_search_grounding.',
  },
  activate_skill: {
    label: t('insights.skillActivation'),
    desc: t('insights.toolDesc.skillActivation'),
  },
  transfer_to_agent: {
    label: t('insights.handOffToSubAgent'),
    desc: t('insights.toolDesc.transferToAgent'),
  },
});

const CALL_KIND_COLORS: Record<string, string> = {
  chat: '#3b82f6',
  embedding: '#10b981',
  title: '#f59e0b',
  summary: '#a855f7',
  insight_extraction: '#ec4899',
  web_search_grounding: '#06b6d4',
  rag_rerank: '#14b8a6',
  file_enrichment: '#84cc16',
  aoa_meta: '#8b5cf6',
  concierge_meta: '#0ea5e9',
  insights_agent: '#6366f1',
  transcription: '#f97316',
  connector_guide: '#d946ef',
  other: '#64748b',
};

// Human-readable label for each call_kind, shown in the "By call kind"
// table instead of the raw backend value. Keep keys in sync with
// CALL_KIND_COLORS and the backend constants in
// backend/modules/observability/models/llm_call_event.py.
const callKindLabels = (t: TFunc): Record<string, string> => ({
  chat: t('insights.callKind.chat'),
  embedding: t('insights.callKind.embedding'),
  title: t('insights.titleGeneration'),
  summary: t('insights.callKind.summary'),
  insight_extraction: t('insights.callKind.insightExtraction'),
  web_search_grounding: t('insights.callKind.webSearchGrounding'),
  rag_rerank: t('insights.callKind.ragRerank'),
  file_enrichment: t('insights.callKind.fileEnrichment'),
  aoa_meta: t('insights.callKind.aoaMeta'),
  concierge_meta: t('insights.callKind.conciergeMeta'),
  insights_agent: t('insights.callKind.insightsAgent'),
  transcription: t('insights.callKind.transcription'),
  connector_guide: t('insights.callKind.connectorGuide'),
  other: t('insights.callKind.other'),
});

/**
 * Stacked-bar tooltip series spec.
 *
 * Order matters: the array is rendered top-to-bottom in the tooltip, and
 * the user has asked us to make that match the visual stack order from
 * BOTTOM to TOP of the column. So the first entry must be the bottom-most
 * <Bar> declared in the chart (it's drawn at y=0). Recharts' default
 * tooltip lists series in PAYLOAD order which can flip relative to the
 * visual stack, so we look values up by dataKey here instead of trusting
 * payload index.
 */
interface StackedSeries {
  dataKey: string;
  name: string;
  color: string;
}
const TOKEN_SERIES: StackedSeries[] = [
  { dataKey: 'input_tokens',          name: 'input (uncached)', color: '#3b82f6' },
  { dataKey: 'cache_read_tokens',     name: 'cache read',       color: '#10b981' },
  { dataKey: 'cache_creation_tokens', name: 'cache write',      color: '#f59e0b' },
  { dataKey: 'output_tokens',         name: 'output',           color: '#a855f7' },
];
const COST_SERIES: StackedSeries[] = [
  { dataKey: 'input_cost_usd',       name: 'input',       color: '#3b82f6' },
  { dataKey: 'cache_read_cost_usd',  name: 'cache read',  color: '#10b981' },
  { dataKey: 'cache_write_cost_usd', name: 'cache write', color: '#f59e0b' },
  { dataKey: 'output_cost_usd',      name: 'output',      color: '#a855f7' },
];

/**
 * Build a Recharts <Tooltip content={…}> render function whose row order
 * follows `series` exactly (i.e. stack-bottom -> stack-top). Optionally
 * appends a bolded "Total" row that sums every series for the hovered day.
 */
function renderStackedTooltip(opts: {
  series: StackedSeries[];
  formatLabel: (raw: unknown) => string;
  formatValue: (n: number) => string;
  includeTotal?: boolean;
}) {
  return function StackedTooltip(props: any) {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- this IS the component
    const { t } = useLanguage();
    if (!props.active || !props.payload?.length) return null;
    const row0 = props.payload[0];
    const datum = (row0 && row0.payload) || {};
    const label = opts.formatLabel(props.label);
    const rows = opts.series.map(s => ({ ...s, value: Number(datum[s.dataKey] ?? 0) }));
    const total = rows.reduce((sum, r) => sum + r.value, 0);
    return (
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid #e5e7eb',
        borderRadius: 4,
        padding: '8px 12px',
        fontSize: 12,
        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
      }}>
        <div style={{ fontWeight: 600, marginBlockEnd: 4 }}>{label}</div>
        {rows.map(r => (
          <div key={r.dataKey} style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
            <span>
              <span style={{
                display: 'inline-block', width: 8, height: 8, background: r.color,
                marginInlineEnd: 6, borderRadius: 2,
              }} />
              {r.name}
            </span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{opts.formatValue(r.value)}</span>
          </div>
        ))}
        {opts.includeTotal && (
          <div style={{
            display: 'flex', gap: 12, justifyContent: 'space-between',
            borderBlockStart: '1px solid #e5e7eb', marginBlockStart: 4, paddingBlockStart: 4,
            fontWeight: 700,
          }}>
            <span>{t('insights.costBreakdown.total')}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{opts.formatValue(total)}</span>
          </div>
        )}
      </div>
    );
  };
}

export function CostBreakdownTab({ filters, onLoadingChange }: CostBreakdownTabProps) {
  const { token } = theme.useToken();
  const { t } = useLanguage();
  // The API-key breakdown is platform-wide by nature (a subtree-scoped key
  // serves many tenants), so it is super-admin only — the backend returns
  // 403 for every other role and we don't even fetch it here.
  const { isSuperAdmin } = useAuth();

  const [componentData, setComponentData] = useState<CostByComponent | null>(null);
  const [callKindData, setCallKindData] = useState<CallKindRow[]>([]);
  const [toolData, setToolData] = useState<ToolRow[]>([]);
  const [providerData, setProviderData] = useState<ProviderRollupRow[]>([]);
  const [apiKeyRows, setApiKeyRows] = useState<ApiKeyCostRow[]>([]);
  const [apiKeyCatalogue, setApiKeyCatalogue] = useState<ApiKeyCatalogueEntry[]>([]);
  // '' = all keys. Otherwise a key UUID, or 'platform' for the env key.
  const [selectedApiKey, setSelectedApiKey] = useState<string>('');
  const [tenantCostRows, setTenantCostRows] = useState<TenantCostRow[]>([]);
  const [artifactData, setArtifactData] = useState<ArtifactFormatRow[]>([]);
  const [artifactTotals, setArtifactTotals] = useState<{
    artifacts: number; edits: number; cost: number;
  }>({ artifacts: 0, edits: 0, cost: 0 });
  const [dailyData, setDailyData] = useState<DailyTimeseriesRow[]>([]);
  const [avgData, setAvgData] = useState<PerConversationAveragesResponse | null>(null);
  // Per-conversation scatter — only fetched when EXACTLY one agent is in
  // the filter. Drives the "Cost per conversation over time" chart that
  // surfaces every single conversation as a dot on a time axis.
  const [convCostPoints, setConvCostPoints] = useState<ConversationCostPoint[]>([]);
  const [loading, setLoading] = useState(true);
  // Tracks whether we've completed at least one fetch. After the first
  // load, filter-change refetches happen in the BACKGROUND — the previous
  // data stays on screen so the user's scroll position is preserved.
  // Returning a full-page <Spin> on every refetch unmounts everything and
  // the browser snaps scrollY back to 0 when the new content mounts.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  // When ON and a single tenant is selected, the cost queries expand the
  // tenant filter to include the full sub-hierarchy (parent + descendants).
  // Off by default for predictability: "the tenant I picked, period."
  // The toggle itself lives in ScopeSelector (inline with the filter row),
  // so its value travels through filters.includeDescendants — we just read it.
  const includeDescendants = filters.includeDescendants ?? false;

  // Scope: the tenant picker is a multi-select, so the "wide" queries
  // (total, daily, averages, provider rollup, artifacts) aggregate across
  // EVERY selected tenant via tenant_ids[]. When none are selected the
  // backend defaults to the caller's full visible scope, so the unfiltered
  // total is always ≥ any single tenant's total.
  // (No memo needed — consumers only read .length / [0] / .join(','), and the
  // load effect already keys on the join string, not array identity.)
  const tenantIds = filters.tenantIds ?? [];
  // A few panels (By call kind, By tool, the per-conversation scatter and
  // timeline) only make sense for ONE tenant — they stay gated on a single
  // selected tenant and fall back to an explanatory empty state otherwise.
  const tenantId = tenantIds.length === 1 ? tenantIds[0] : undefined;
  // The parent dashboard packs cross-tenant agents that share a name into a
  // single Select option whose value is comma-joined UUIDs (e.g. "uuid1,uuid2").
  // Backend endpoints expect flat UUID arrays, so we expand the comma-strings
  // here BEFORE handing chatbotIds to any API call. Without this expansion,
  // picking a merged agent sends "uuid1,uuid2" as a single param value and
  // the backend's UUID(...) parse fails with HTTP 500 → "Failed to load".
  const chatbotIds = useMemo(
    () => (filters.chatbotIds ?? []).flatMap((v) => v.split(',').map((s) => s.trim()).filter(Boolean)),
    [filters.chatbotIds?.join(',')],
  );
  const chatbotId = chatbotIds[0];  // for hint text; backend uses the full array

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      onLoadingChange?.(true);
      try {
        // One scope object drives all the "wide" (multi-tenant-aware)
        // queries. include_descendants only matters when at least one tenant
        // is picked; with none picked the backend already returns the full
        // visible scope.
        const scope = {
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          tenantIds,
          chatbotIds,
          includeDescendants: tenantIds.length > 0 ? includeDescendants : false,
        };

        const [byComp, providerRows, daily, averages, artifacts, byKey, byTenant] = await Promise.all([
          insightsService.getCostByComponent(scope),
          insightsService.getProviderRollup(scope),
          insightsService.getDailyTimeseries(scope),
          insightsService.getPerConversationAverages(scope),
          insightsService.getArtifacts(scope),
          // Super-admin only — a group-admin request would 403 and reject the
          // whole Promise.all, blanking the page. Resolve to empty instead.
          isSuperAdmin
            ? insightsService.getCostByApiKey({
                ...scope,
                apiKeyId: selectedApiKey || undefined,
              })
            : Promise.resolve({ rows: [] } as any),
          insightsService.getCostByTenant(scope),
        ]);

        const tenantScopedExtras = tenantId
          ? await Promise.all([
              insightsService.getCostByCallKind({
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                tenantId,
                chatbotIds,
              }),
              insightsService.getCostByTool({
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                tenantId,
                chatbotIds,
              }),
            ])
          : [null, null];

        if (cancelled) return;
        setComponentData(byComp);
        setProviderData(providerRows.rows);
        setApiKeyRows(byKey.rows ?? []);
        setTenantCostRows(byTenant.rows ?? []);
        setArtifactData(artifacts.by_format ?? []);
        setArtifactTotals({
          artifacts: artifacts.total_artifacts ?? 0,
          edits: artifacts.total_edits ?? 0,
          cost: artifacts.total_cost_usd ?? 0,
        });
        setCallKindData(tenantScopedExtras[0]?.breakdown ?? []);
        setToolData(tenantScopedExtras[1]?.tools ?? []);
        setDailyData(daily.days ?? []);
        setAvgData(averages);

        // Per-conversation scatter: only meaningful for a single logical
        // agent. `filters.chatbotIds` carries one entry per logical agent
        // (even if that agent's value is comma-joined UUIDs for cross-tenant
        // merging), so we check the RAW length here, not the expanded one.
        const logicalAgentCount = (filters.chatbotIds ?? []).length;
        if (logicalAgentCount === 1) {
          try {
            const ts = await insightsService.getConversationsCostTimeseries({
              dateFrom: filters.dateFrom,
              dateTo: filters.dateTo,
              tenantIds,
              chatbotIds,
            });
            if (!cancelled) setConvCostPoints(ts.conversations ?? []);
          } catch {
            if (!cancelled) setConvCostPoints([]);
          }
        } else {
          setConvCostPoints([]);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('CostBreakdownTab load error:', err);
        antdMessage.error(t('insights.costBreakdown.loadFailed'));
      } finally {
        if (!cancelled) {
          setLoading(false);
          setHasLoadedOnce(true);
          onLoadingChange?.(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // Arrays are joined into key strings for stable dependency tracking.
    // Depend on the FULL tenant list (not just the first tenant) so adding or
    // removing any tenant in the multi-select triggers a refetch — the old
    // `tenantId` (first only) dep silently ignored 2nd+ tenant changes.
  }, [filters.dateFrom, filters.dateTo, tenantIds.join(','), chatbotIds.join(','),
      includeDescendants, isSuperAdmin, selectedApiKey]);

  // Key catalogue is independent of the date/tenant filters — fetch once.
  // Super-admin only; the endpoint 403s for everyone else.
  useEffect(() => {
    if (!isSuperAdmin) {
      setApiKeyCatalogue([]);
      return;
    }
    let cancelled = false;
    insightsService.getApiKeyCatalogue()
      .then((res) => {
        if (!cancelled) setApiKeyCatalogue(res.keys ?? []);
      })
      .catch(() => {
        // A missing catalogue only costs the picker its labels — the
        // breakdown itself still renders from the ledger rows.
        if (!cancelled) setApiKeyCatalogue([]);
      });
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  // KPI derivations
  const totalCost = componentData?.cost_breakdown.total_cost_usd ?? 0;
  const deletedConversations = componentData?.deleted_conversations;
  // Uplift that keeps the headline at or above the expected invoice. See
  // PricingService.invoice_safety — the ledger can only price calls it
  // recorded, so the measured total is structurally a floor, not the bill.
  const safety = componentData?.invoice_safety;
  const cacheSavingsPct = componentData?.cache_savings_pct ?? 0;
  const callCount = componentData?.call_count ?? 0;

  const pieData = useMemo(() => {
    if (!componentData) return [];
    const b = componentData.cost_breakdown;
    return [
      { name: t('insights.costBreakdown.input'), value: b.input_cost_usd, color: '#3b82f6' },
      { name: t('insights.costBreakdown.output'), value: b.output_cost_usd, color: '#a855f7' },
      { name: t('agentDebug.titleCacheRead'), value: b.cache_read_cost_usd, color: '#10b981' },
      { name: t('agentDebug.titleCacheWrite'), value: b.cache_write_cost_usd, color: '#f59e0b' },
      { name: t('insights.costBreakdown.webSearch'), value: b.web_search_cost_usd, color: '#ec4899' },
    ].filter(d => d.value > 0);
  }, [componentData]);

  // ── By API key derivations ────────────────────────────────────────────
  // The backend returns one row per key × provider × day. We derive:
  //  (a) a per-key summary (label, provider, calls, first/last use, total)
  //  (b) a per-day pivot { day, [keyId]: cost } for the stacked switchover
  //      chart — a key switchover shows as a color change mid-window.
  const apiKeySummary = useMemo(() => {
    const byKey = new Map<string, {
      keyId: string;
      label: string;
      provider: string;
      isPlatform: boolean;
      calls: number;
      total: number;
      firstUse: string | null;
      lastUse: string | null;
    }>();
    // The server says which key served each row; a deleted key's spend stays
    // its own (the ledger keeps api_key_id as a tombstone), never the platform's.
    const keyLabel = (r: ApiKeyCostRow): string => {
      if (r.key_state === 'registered') return `${r.key_name} (…${r.key_hint ?? ''})`;
      if (r.key_state === 'deleted') {
        return t('insights.cost.byApiKey.deletedKey', {
          provider: r.provider, id: (r.api_key_id ?? '').slice(0, 8),
        });
      }
      return t('insights.cost.byApiKey.platformKey', { provider: r.provider });
    };
    for (const r of apiKeyRows) {
      const keyId = r.api_key_id ?? `platform:${r.provider}`;
      const label = keyLabel(r);
      const e = byKey.get(keyId) ?? {
        keyId,
        label,
        provider: r.provider,
        isPlatform: !r.api_key_id,
        calls: 0,
        total: 0,
        firstUse: null as string | null,
        lastUse: null as string | null,
      };
      e.calls += r.call_count;
      e.total += r.total_cost_usd;
      if (r.first_call_at && (!e.firstUse || r.first_call_at < e.firstUse)) e.firstUse = r.first_call_at;
      if (r.last_call_at && (!e.lastUse || r.last_call_at > e.lastUse)) e.lastUse = r.last_call_at;
      byKey.set(keyId, e);
    }
    return Array.from(byKey.values()).sort((a, b) => b.total - a.total);
  }, [apiKeyRows, t]);

  const apiKeyDaily = useMemo(() => {
    const byDay = new Map<string, Record<string, number | string>>();
    for (const r of apiKeyRows) {
      const keyId = r.api_key_id ?? `platform:${r.provider}`;
      const e = byDay.get(r.day) ?? { day: r.day };
      e[keyId] = (Number(e[keyId]) || 0) + r.total_cost_usd;
      byDay.set(r.day, e);
    }
    return Array.from(byDay.values()).sort((a, b) =>
      String(a.day).localeCompare(String(b.day)));
  }, [apiKeyRows]);

  // Distinct color per key, assigned by spend rank so the biggest keys get
  // the most distinguishable colors.
  const API_KEY_PALETTE = ['#3b82f6', '#f59e0b', '#10b981', '#a855f7', '#ef4444', '#06b6d4', '#84cc16', '#ec4899'];
  const apiKeyColor = (keyId: string): string => {
    const idx = apiKeySummary.findIndex((k) => k.keyId === keyId);
    return API_KEY_PALETTE[(idx < 0 ? 0 : idx) % API_KEY_PALETTE.length];
  };

  const tenantCostTotal = useMemo(
    () => tenantCostRows.reduce((sum, r) => sum + r.total_cost_usd, 0),
    [tenantCostRows],
  );

  // Picker options: every key in the platform, labelled the way the admin UI
  // labels it, annotated with who it actually serves. Value '' = all keys,
  // 'platform' = the env key (whose ledger rows carry api_key_id NULL).
  const apiKeyOptions = useMemo(() => {
    const describe = (k: ApiKeyCatalogueEntry): string => {
      if (k.is_platform_key) return t('insights.cost.byApiKey.servesPlatform');
      const tenant = k.tenant_name ?? k.tenant_id ?? '—';
      return k.key_scope === 'subtree'
        ? t('insights.cost.byApiKey.servesSubtree', { tenant })
        : t('insights.cost.byApiKey.servesTenant', { tenant });
    };
    const options = apiKeyCatalogue
      .filter((k) => !k.is_platform_key)
      .map((k) => ({
        value: k.api_key_id ?? '',
        provider: k.provider,
        label: `${k.key_name} (…${k.key_hint ?? ''})`,
        description: describe(k),
        status: k.key_status,
      }));
    // The catalogue lists the platform key once per provider, but the ledger
    // records all of them in a single api_key_id IS NULL bucket — so it is
    // ONE selectable option ('platform'), not one per provider. Picking it
    // shows every provider's platform-key spend, split by provider in the
    // rows underneath.
    if (apiKeyCatalogue.some((k) => k.is_platform_key)) {
      options.push({
        value: 'platform',
        provider: '—',
        label: t('insights.cost.byApiKey.platformKey', { provider: 'env' }),
        description: t('insights.cost.byApiKey.servesPlatform'),
        status: 'active',
      });
    }
    return options;
  }, [apiKeyCatalogue, t]);

  const selectedKeyLabel = useMemo(() => {
    if (!selectedApiKey) return null;
    return apiKeyOptions.find((o) => o.value === selectedApiKey)?.label ?? selectedApiKey;
  }, [selectedApiKey, apiKeyOptions]);

  // One row per day for the selected key — the "cost over time" series.
  // apiKeyRows is already server-filtered when a key is selected, so this
  // just collapses provider splits into a single daily total.
  const selectedKeyDaily = useMemo(() => {
    const byDay = new Map<string, {
      day: string; input: number; output: number;
      read: number; write: number; total: number; calls: number;
    }>();
    for (const r of apiKeyRows) {
      const e = byDay.get(r.day) ?? {
        day: r.day, input: 0, output: 0, read: 0, write: 0, total: 0, calls: 0,
      };
      e.input += r.input_cost_usd;
      e.output += r.output_cost_usd;
      e.read += r.cache_read_cost_usd;
      e.write += r.cache_write_cost_usd;
      e.total += r.total_cost_usd;
      e.calls += r.call_count;
      byDay.set(r.day, e);
    }
    return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [apiKeyRows]);

  const selectedKeyTotal = useMemo(
    () => selectedKeyDaily.reduce((sum, d) => sum + d.total, 0),
    [selectedKeyDaily],
  );

  const callKindColumns: ColumnsType<CallKindRow> = [
    {
      title: t('insights.callKindColumn'),
      dataIndex: 'call_kind',
      render: (k: string) => (
        <Tag color={CALL_KIND_COLORS[k] ?? CALL_KIND_COLORS.other}>
          {callKindLabels(t)[k] ?? k}
        </Tag>
      ),
    },
    {
      title: t('insights.agentStats.cost'),
      dataIndex: 'total_cost_usd',
      align: 'right',
      sorter: (a, b) => a.total_cost_usd - b.total_cost_usd,
      defaultSortOrder: 'descend',
      render: fmtUsd,
    },
    {
      title: t('insights.messageBreakdown.inputTokens'),
      dataIndex: 'input_tokens',
      align: 'right',
      render: fmtN,
    },
    {
      title: t('insights.messageBreakdown.outputTokens'),
      dataIndex: 'output_tokens',
      align: 'right',
      render: fmtN,
    },
    {
      title: t('agentDebug.calls'),
      dataIndex: 'call_count',
      align: 'right',
      render: fmtN,
    },
  ];

  const toolColumns: ColumnsType<ToolRow> = [
    {
      title: t('insights.tool'),
      dataIndex: 'tool_name',
      render: (name: string) => {
        const meta = toolDescriptions(t)[name];
        if (!meta) {
          return <Text style={{ fontFamily: 'monospace' }}>{name}</Text>;
        }
        return (
          <AntdTooltip title={meta.desc}>
            <div>
              <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{name}</Text>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }} dir="auto">{meta.label}</Text>
              </div>
            </div>
          </AntdTooltip>
        );
      },
    },
    {
      title: t('agentDebug.calls'),
      dataIndex: 'call_count',
      align: 'right',
      sorter: (a, b) => a.call_count - b.call_count,
      render: fmtN,
    },
    {
      title: t('insights.messageBreakdown.inputTokens'),
      dataIndex: 'input_tokens',
      align: 'right',
      sorter: (a, b) => a.input_tokens - b.input_tokens,
      render: fmtN,
    },
    {
      title: t('insights.messageBreakdown.outputTokens'),
      dataIndex: 'output_tokens',
      align: 'right',
      sorter: (a, b) => a.output_tokens - b.output_tokens,
      render: fmtN,
    },
    {
      title: t('insights.messageBreakdown.cacheRead'),
      dataIndex: 'cache_read_tokens',
      align: 'right',
      sorter: (a, b) => a.cache_read_tokens - b.cache_read_tokens,
      render: fmtN,
    },
    {
      title: t('insights.agentStats.cost'),
      dataIndex: 'total_cost_usd',
      align: 'right',
      sorter: (a, b) => a.total_cost_usd - b.total_cost_usd,
      defaultSortOrder: 'descend',
      render: fmtUsd,
    },
  ];

  const ARTIFACT_FORMAT_LABELS: Record<string, string> = {
    pptx: 'Presentation (.pptx)',
    docx: 'Document (.docx)',
    markdown: 'Markdown (.md)',
    html: t('insights.artifactFormat.html'),
    gforms: t('insights.artifactFormat.gforms'),
  };

  const artifactColumns: ColumnsType<ArtifactFormatRow> = [
    {
      title: t('insights.kind'),
      dataIndex: 'format',
      render: (fmt: string) => (
        <div>
          <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmt}</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {ARTIFACT_FORMAT_LABELS[fmt] ?? fmt}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: t('admin.created'),
      dataIndex: 'artifacts',
      align: 'right',
      sorter: (a, b) => a.artifacts - b.artifacts,
      render: fmtN,
    },
    {
      title: t('insights.edits'),
      dataIndex: 'edits',
      align: 'right',
      sorter: (a, b) => a.edits - b.edits,
      render: fmtN,
    },
    {
      title: t('insights.attributedCost'),
      dataIndex: 'cost_usd',
      align: 'right',
      sorter: (a, b) => a.cost_usd - b.cost_usd,
      defaultSortOrder: 'descend',
      render: fmtUsd,
    },
  ];

  // ONLY blank the page on the very first load. Subsequent filter changes
  // keep the previous data on screen and revalidate in the background, so
  // the user's scroll position is preserved.
  if (loading && !hasLoadedOnce) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!componentData || componentData.call_count === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* CostMonitorCutoverBanner removed per UX request */}
        <Empty
          description={t('insights.costBreakdown.noLlmCalls')}
          style={{ padding: 80 }}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, position: 'relative' }}>
      {/* The "Refreshing…" indicator is rendered by ScopeSelector (in the
          filter row up top) via the onLoadingChange callback. No in-tab pill. */}

      {/* Sub-tenants toggle now lives in ScopeSelector (inline with the
          filter row up top), so there's no extra chip rendered down here. */}

      {/* KPI Cards Row — every card is identical height + layout so the
          rectangles align even when the secondary caption text wraps over
          multiple lines. We force display:flex on each Col so the Card
          inside stretches to fill the row, then flex-justify the body so
          the value sits at the top and the caption pins to the bottom. */}
      {(() => {
        const colSpan = { xs: 24, sm: 12, md: 6 };
        const cardStyle: React.CSSProperties = { width: '100%', height: '100%' };
        const bodyStyle: React.CSSProperties = {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          gap: 8,
        };
        const captionStyle: React.CSSProperties = {
          fontSize: 12,
          minHeight: 32,    // = 2 lines @ 12px line-height — caption rows stay aligned
          display: 'block',
        };
        return (
          <Row gutter={[16, 16]} align="stretch">
            <Col {...colSpan} style={{ display: 'flex' }}>
              <Card style={cardStyle} styles={{ body: bodyStyle }}>
                <Statistic
                  title={
                    safety?.enabled
                      ? (
                        <AntdTooltip title={t('insights.cost.invoiceSafety.tooltip')}>
                          <span>{t('insights.cost.invoiceSafety.title')}</span>
                        </AntdTooltip>
                      )
                      : t('insights.cost.kpi.totalCost')
                  }
                  value={safety?.enabled ? safety.invoice_safe_cost_usd : totalCost}
                  formatter={(v) => fmtUsd(Number(v))}
                  valueStyle={{ color: token.colorPrimary }}
                />
                <Text type="secondary" style={captionStyle}>
                  {/* The measured figure stays visible: the uplift is an
                      explicit allowance, not a silent adjustment. */}
                  {safety?.enabled ? (
                    <>
                      {t('insights.cost.invoiceSafety.measured', { amount: fmtUsd(totalCost) })}
                      {' · '}
                      {t('insights.cost.invoiceSafety.uplift', { pct: safety.total_pct })}
                    </>
                  ) : (
                    <>{t('insights.cost.kpi.providerCalls', { count: fmtN(callCount) })}</>
                  )}
                </Text>
                {/* Deleted conversations keep their cost rows, so this is a part of
                    the figure above — said out loud so the total is never a mystery. */}
                {deletedConversations && deletedConversations.conversations > 0 && (
                  <Text type="secondary" style={captionStyle}>
                    {t('insights.cost.kpi.fromDeletedConversations', {
                      count: deletedConversations.conversations,
                      amount: fmtUsd(deletedConversations.cost_usd),
                    })}
                  </Text>
                )}
              </Card>
            </Col>
            <Col {...colSpan} style={{ display: 'flex' }}>
              <Card style={cardStyle} styles={{ body: bodyStyle }}>
                <Statistic
                  title={t('insights.costBreakdown.conversations')}
                  value={avgData?.conversation_count ?? 0}
                  formatter={(v) => fmtN(Number(v))}
                />
                <Text type="secondary" style={captionStyle}>
                  chatted with this agent during the selected window
                </Text>
              </Card>
            </Col>
            <Col {...colSpan} style={{ display: 'flex' }}>
              <Card style={cardStyle} styles={{ body: bodyStyle }}>
                <Statistic
                  title={t('insights.costBreakdown.cacheDiscount')}
                  value={cacheSavingsPct}
                  precision={1}
                  suffix="%"
                  valueStyle={{ color: cacheSavingsPct > 70 ? '#10b981' : '#f59e0b' }}
                />
                <Text type="secondary" style={captionStyle}>
                  share of input tokens billed at the cheaper cache-read rate
                </Text>
              </Card>
            </Col>
            <Col {...colSpan} style={{ display: 'flex' }}>
              <Card style={cardStyle} styles={{ body: bodyStyle }}>
                <Statistic
                  title={t('insights.costBreakdown.avgCostPerConversation')}
                  value={avgData?.avg_total_cost_usd ?? 0}
                  formatter={(v) => fmtUsd(Number(v))}
                  valueStyle={{ color: token.colorPrimary }}
                />
                <Text type="secondary" style={captionStyle}>
                  {avgData?.conversation_count
                    ? `${fmtN(avgData.conversation_count)} conversations sampled`
                    : 'no conversations in this window'}
                </Text>
              </Card>
            </Col>
          </Row>
        );
      })()}

      {/* Response-time row — surfaces how long the user waits, alongside
          the cost. Two cards: Time-To-First-Token (when the AI started
          streaming) and total wall time (when the response was complete).
          Each card shows median + p95 so the user sees both the typical
          experience and the worst-case tail. */}
      {componentData.response_time && (
        (() => {
          const rt = componentData.response_time!;
          const fmtMs = (v: number | null | undefined): string => {
            if (v == null) return '—';
            if (v < 1000) return `${Math.round(v)} ms`;
            return `${(v / 1000).toFixed(1)} s`;
          };
          const cardStyle: React.CSSProperties = {
            width: '100%',
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${token.colorBorderSecondary}`,
          };
          const bodyStyle: React.CSSProperties = {
            padding: '12px 16px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: 130,
          };
          const captionStyle: React.CSSProperties = {
            fontSize: 12,
            minHeight: 32,
            display: 'block',
          };
          return (
            <Row gutter={[16, 16]} align="stretch" style={{ marginTop: 12 }}>
              <Col xs={24} md={12} style={{ display: 'flex' }}>
                <Card style={cardStyle} styles={{ body: bodyStyle }}>
                  <Statistic
                    title={t('insights.costBreakdown.ttft')}
                    value={fmtMs(rt.p50_first_token_ms)}
                    valueStyle={{ color: token.colorPrimary }}
                  />
                  <Text type="secondary" style={captionStyle}>
                    when the AI started streaming · median across {fmtN(callCount)} calls
                    {rt.p95_first_token_ms != null && (
                      <> · p95 {fmtMs(rt.p95_first_token_ms)}</>
                    )}
                  </Text>
                </Card>
              </Col>
              <Col xs={24} md={12} style={{ display: 'flex' }}>
                <Card style={cardStyle} styles={{ body: bodyStyle }}>
                  <Statistic
                    title={t('insights.costBreakdown.responseTime')}
                    value={fmtMs(rt.p50_duration_ms)}
                    valueStyle={{ color: token.colorPrimary }}
                  />
                  <Text type="secondary" style={captionStyle}>
                    when the response was complete · median across {fmtN(callCount)} calls
                    {rt.p95_duration_ms != null && (
                      <> · p95 {fmtMs(rt.p95_duration_ms)}</>
                    )}
                  </Text>
                </Card>
              </Col>
            </Row>
          );
        })()
      )}

      {/* Token + Cost composition stacked bars — answers "what % of the
          bill is each category" without making the user squint at small
          numbers. The two bars share a color legend; comparing them tells
          you whether caching is paying off (large green wedge on the
          tokens bar but small green wedge on the cost bar = caching wins). */}
      <TokenCompositionBar
        tokenBreakdown={componentData.token_breakdown}
        costBreakdown={componentData.cost_breakdown}
      />

      {/* Per-conversation averages first — the broad statistical view
          across every conversation in scope. Below it sits the median-
          conversation card (what a single typical conv looks like). */}
      {avgData && avgData.conversation_count > 0 && (
        <Card
          title={t('insights.costBreakdown.perConversationAverages')}
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('insights.acrossConversations', { count: avgData.conversation_count })}
            </Text>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={6} lg={4}>
              <Statistic
                title={t('insights.costBreakdown.avgCostPerConversation')}
                value={avgData.avg_total_cost_usd}
                precision={6}
                prefix="$"
                valueStyle={{ color: token.colorPrimary }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                p50 {fmtUsd(avgData.p50_cost_usd)} · p90 {fmtUsd(avgData.p90_cost_usd)}
              </Text>
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <Statistic
                title={t('insights.costBreakdown.avgInputTokens')}
                value={fmtN(avgData.avg_input_tokens)}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                uncached portion
              </Text>
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <Statistic
                title={t('insights.costBreakdown.avgOutputTokens')}
                value={fmtN(avgData.avg_output_tokens)}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                what the model writes back per conversation
              </Text>
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <Statistic
                title={t('insights.costBreakdown.avgCachedTokens')}
                value={fmtN(avgData.avg_cache_read_tokens)}
                valueStyle={{
                  color: avgData.avg_cache_read_tokens > 0 ? '#10b981' : undefined,
                }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {(avgData.cache_hit_ratio * 100).toFixed(1)}% of avg input
              </Text>
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <Statistic
                title={t('insights.costBreakdown.avgCacheWrite')}
                value={fmtN(avgData.avg_cache_creation_tokens)}
                valueStyle={{
                  color: avgData.avg_cache_creation_tokens > 0 ? '#f59e0b' : undefined,
                }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                billed at ~2x rate
              </Text>
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <Statistic
                title={t('insights.costBreakdown.avgLlmCalls')}
                value={avgData.avg_calls_per_conversation.toFixed(1)}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                includes embeddings, RAG, tools
              </Text>
            </Col>
          </Row>
          <div style={{ marginTop: 12, padding: '8px 12px', background: token.colorBgLayout, borderRadius: 4, fontSize: 12, color: token.colorTextSecondary }}>
            {t('insights.costBreakdown.useAveragesHint')}
          </div>
        </Card>
      )}

      {/* Median-conversation card — what a SINGLE typical conversation looks
          like. Lives below the broad averages so the eye moves from
          aggregate → exemplar. */}
      {avgData && avgData.conversation_count > 0 && (
        <TypicalConversationCard averages={avgData} />
      )}

      {/* Daily charts: tokens AND cost over time, respecting all filters */}
      {dailyData.length > 0 && (
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Card title={t('insights.costBreakdown.tokensPerDay')}>
              <ResponsiveContainer width="100%" height={280}>
                {/* Left margin keeps long Y-axis labels (e.g. "1,234,567")
                    from being clipped against the card edge. */}
                <BarChart data={dailyData} margin={{ top: 8, right: 12, bottom: 4, left: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(d) => dayjs(d).format(datePattern('monthDay'))}
                  />
                  <YAxis width={64} tick={{ fontSize: 11 }} tickFormatter={(v) => fmtN(v as number)} />
                  {/* Custom tooltip pinned to the visual stack order
                      (bottom -> top): input, cache read, cache write,
                      output. Recharts' default tooltip lists series in
                      payload order which is the OPPOSITE of the column,
                      which made the legend swatches and tooltip rows mismatch. */}
                  <RechartsTooltip
                    content={renderStackedTooltip({
                      series: TOKEN_SERIES,
                      formatLabel: (d) => dayjs(d as string).format(datePattern('monthDayYear')),
                      formatValue: fmtN,
                    })}
                  />
                  <Legend />
                  <Bar dataKey="input_tokens" stackId="t" fill="#3b82f6" name="input (uncached)" />
                  <Bar dataKey="cache_read_tokens" stackId="t" fill="#10b981" name="cache read" />
                  <Bar dataKey="cache_creation_tokens" stackId="t" fill="#f59e0b" name="cache write" />
                  <Bar dataKey="output_tokens" stackId="t" fill="#a855f7" name="output" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title={t('insights.costBreakdown.aiCostPerDay')}>
              <ResponsiveContainer width="100%" height={280}>
                {/* Left margin + wider YAxis so currency labels (e.g.
                    "$0.0001234") don't get truncated. */}
                <BarChart data={dailyData} margin={{ top: 8, right: 12, bottom: 4, left: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(d) => dayjs(d).format(datePattern('monthDay'))}
                  />
                  <YAxis width={72} tick={{ fontSize: 11 }} tickFormatter={(v) => fmtUsd(v as number)} />
                  {/* Same pinned order as Tokens-per-day so the two charts
                      have IDENTICAL row sequences in the hover tooltip.
                      Plus a "Total" row at the bottom for the day's full bill. */}
                  <RechartsTooltip
                    content={renderStackedTooltip({
                      series: COST_SERIES,
                      formatLabel: (d) => dayjs(d as string).format(datePattern('monthDayYear')),
                      formatValue: fmtUsd,
                      includeTotal: true,
                    })}
                  />
                  <Legend />
                  <Bar dataKey="input_cost_usd" stackId="c" fill="#3b82f6" name="input" />
                  <Bar dataKey="cache_read_cost_usd" stackId="c" fill="#10b981" name="cache read" />
                  <Bar dataKey="cache_write_cost_usd" stackId="c" fill="#f59e0b" name="cache write" />
                  <Bar dataKey="output_cost_usd" stackId="c" fill="#a855f7" name="output" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      )}

      {/* ─── Cost by API key ───────────────────────────────────────────────
          The "keys separation" view: one stacked series per serving key on
          a daily axis + a summary table. A tenant-key switchover (like the
          2026-07 platform-key → M² key move) reads as a clean color change
          instead of requiring a database forensic session. */}
      {isSuperAdmin && (
      <Card
        title={
          <Space size={8}>
            {t('insights.cost.byApiKey.title')}
            <Tag color="gold">{t('insights.cost.byApiKey.superAdminOnly')}</Tag>
          </Space>
        }
        extra={
          <Space size={8} wrap>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('insights.cost.byApiKey.filterLabel')}
            </Text>
            <Select
              value={selectedApiKey}
              onChange={setSelectedApiKey}
              style={{ minWidth: 320 }}
              size="small"
              showSearch
              optionFilterProp="label"
              options={[
                { value: '', label: t('insights.cost.byApiKey.allKeys') },
                ...apiKeyOptions.map((o) => ({
                  value: o.value,
                  label: o.label,
                  // Second line gives the answer the picker exists for:
                  // WHO this key serves, not just what it's called.
                  title: o.description,
                })),
              ]}
              optionRender={(option) => {
                const meta = apiKeyOptions.find((o) => o.value === option.value);
                return (
                  <div>
                    <div style={{ fontSize: 13 }} dir="auto">{option.label}</div>
                    {meta && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {meta.provider !== '—' ? `${meta.provider} · ` : ''}{meta.description}
                      </Text>
                    )}
                  </div>
                );
              }}
            />
          </Space>
        }
      >
        {apiKeySummary.length === 0 ? (
          <Empty
            description={
              selectedApiKey
                ? t('insights.cost.byApiKey.selectedEmpty')
                : t('insights.cost.byApiKey.empty')
            }
            style={{ padding: 24 }}
          />
        ) : selectedApiKey ? (
          /* ── Single-key view ──────────────────────────────────────────
             One key chosen: a cost-over-time chart and a day-by-day table
             for that key alone, in the shape the provider console shows. */
          <>
            <Text strong style={{ display: 'block', marginBlockEnd: 8 }}>
              {t('insights.cost.byApiKey.singleKeyChartTitle', { key: selectedKeyLabel })}
              {' · '}
              <span style={{ color: token.colorPrimary }}>{fmtUsd(selectedKeyTotal)}</span>
            </Text>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={selectedKeyDaily} margin={{ top: 8, right: 12, bottom: 4, left: 12 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(d) => dayjs(d).format(datePattern('monthDay'))}
                />
                <YAxis width={72} tick={{ fontSize: 11 }} tickFormatter={(v) => fmtUsd(v as number)} />
                <RechartsTooltip
                  content={renderStackedTooltip({
                    series: [
                      { dataKey: 'input', name: t('insights.cost.byApiKey.columns.input'), color: '#3b82f6' },
                      { dataKey: 'read', name: t('insights.cost.byApiKey.columns.cacheRead'), color: '#10b981' },
                      { dataKey: 'write', name: t('insights.cost.byApiKey.columns.cacheWrite'), color: '#f59e0b' },
                      { dataKey: 'output', name: t('insights.cost.byApiKey.columns.output'), color: '#a855f7' },
                    ],
                    formatLabel: (d) => dayjs(d as string).format(datePattern('monthDayYear')),
                    formatValue: fmtUsd,
                    includeTotal: true,
                  })}
                />
                <Legend />
                <Bar dataKey="input" stackId="s" fill="#3b82f6" name={t('insights.cost.byApiKey.columns.input')} />
                <Bar dataKey="read" stackId="s" fill="#10b981" name={t('insights.cost.byApiKey.columns.cacheRead')} />
                <Bar dataKey="write" stackId="s" fill="#f59e0b" name={t('insights.cost.byApiKey.columns.cacheWrite')} />
                <Bar dataKey="output" stackId="s" fill="#a855f7" name={t('insights.cost.byApiKey.columns.output')} />
              </BarChart>
            </ResponsiveContainer>
            <Text strong style={{ display: 'block', marginBlockStart: 16, marginBlockEnd: 8 }}>
              {t('insights.cost.byApiKey.singleKeyTableTitle', { key: selectedKeyLabel })}
            </Text>
            <Table
              rowKey="day"
              dataSource={selectedKeyDaily}
              size="small"
              pagination={false}
              scroll={{ y: 360 }}
              columns={[
                {
                  title: t('insights.cost.byApiKey.columns.day'),
                  dataIndex: 'day',
                  render: (d: string) => (
                    <Text style={{ fontSize: 12 }}>{dayjs(d).format(datePattern('monthDayYear'))}</Text>
                  ),
                },
                { title: t('insights.cost.byApiKey.columns.calls'), dataIndex: 'calls',
                  align: 'right' as const, width: 90, render: fmtN },
                { title: t('insights.cost.byApiKey.columns.input'), dataIndex: 'input',
                  align: 'right' as const, width: 110, render: fmtUsd },
                { title: t('insights.cost.byApiKey.columns.cacheRead'), dataIndex: 'read',
                  align: 'right' as const, width: 110, render: fmtUsd },
                { title: t('insights.cost.byApiKey.columns.cacheWrite'), dataIndex: 'write',
                  align: 'right' as const, width: 110, render: fmtUsd },
                { title: t('insights.cost.byApiKey.columns.output'), dataIndex: 'output',
                  align: 'right' as const, width: 110, render: fmtUsd },
                { title: t('insights.cost.byApiKey.columns.total'), dataIndex: 'total',
                  align: 'right' as const, width: 120,
                  render: (v: number) => <strong>{fmtUsd(v)}</strong> },
              ]}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6}>
                    <strong>{t('insights.cost.byApiKey.columns.total')}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <strong>{fmtUsd(selectedKeyTotal)}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={apiKeyDaily} margin={{ top: 8, right: 12, bottom: 4, left: 12 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(d) => dayjs(d).format(datePattern('monthDay'))}
                />
                <YAxis width={72} tick={{ fontSize: 11 }} tickFormatter={(v) => fmtUsd(v as number)} />
                <RechartsTooltip
                  content={renderStackedTooltip({
                    series: apiKeySummary.map((k) => ({
                      dataKey: k.keyId,
                      name: k.label,
                      color: apiKeyColor(k.keyId),
                    })),
                    formatLabel: (d) => dayjs(d as string).format(datePattern('monthDayYear')),
                    formatValue: fmtUsd,
                    includeTotal: true,
                  })}
                />
                <Legend />
                {apiKeySummary.map((k) => (
                  <Bar
                    key={k.keyId}
                    dataKey={k.keyId}
                    stackId="k"
                    fill={apiKeyColor(k.keyId)}
                    name={k.label}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBlockEnd: 12 }}>
              {t('insights.cost.byApiKey.chartHint')}
            </Text>
            <Table
              rowKey="keyId"
              dataSource={apiKeySummary}
              size="small"
              pagination={false}
              columns={[
                {
                  title: t('insights.cost.byApiKey.columns.key'),
                  dataIndex: 'label',
                  render: (label: string, r: typeof apiKeySummary[number]) => (
                    <Space size={6}>
                      <span style={{
                        display: 'inline-block', width: 10, height: 10,
                        background: apiKeyColor(r.keyId), borderRadius: 2,
                      }} />
                      <Text style={{ fontSize: 12 }}>{label}</Text>
                    </Space>
                  ),
                },
                {
                  title: t('insights.cost.byApiKey.columns.provider'),
                  dataIndex: 'provider',
                  width: 120,
                  render: (p: string) => <Tag>{p}</Tag>,
                },
                {
                  title: t('insights.cost.byApiKey.columns.firstUse'),
                  dataIndex: 'firstUse',
                  width: 140,
                  render: (v: string | null) => (
                    <Text style={{ fontSize: 12 }}>{v ? dayjs(v).format(datePattern('monthDayTime')) : '—'}</Text>
                  ),
                },
                {
                  title: t('insights.cost.byApiKey.columns.lastUse'),
                  dataIndex: 'lastUse',
                  width: 140,
                  render: (v: string | null) => (
                    <Text style={{ fontSize: 12 }}>{v ? dayjs(v).format(datePattern('monthDayTime')) : '—'}</Text>
                  ),
                },
                {
                  title: t('insights.cost.byApiKey.columns.calls'),
                  dataIndex: 'calls',
                  align: 'right' as const,
                  width: 90,
                  render: fmtN,
                },
                {
                  title: t('insights.cost.byApiKey.columns.total'),
                  dataIndex: 'total',
                  align: 'right' as const,
                  width: 120,
                  defaultSortOrder: 'descend' as const,
                  sorter: (a: typeof apiKeySummary[number], b: typeof apiKeySummary[number]) => a.total - b.total,
                  render: (v: number) => <strong>{fmtUsd(v)}</strong>,
                },
              ]}
            />
          </>
        )}
      </Card>
      )}

      {/* ─── Cost by tenant ────────────────────────────────────────────────
          Only rendered when the active scope actually spans more than one
          tenant — for a single tenant it would repeat the headline total. */}
      {tenantCostRows.length > 1 && (
        <Card
          title={t('insights.cost.byTenant.title')}
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('insights.cost.byTenant.subtitle')}
            </Text>
          }
        >
          <Table
            rowKey={(r: TenantCostRow) => r.tenant_id ?? 'unknown'}
            dataSource={tenantCostRows}
            size="small"
            pagination={false}
            columns={[
              {
                title: t('insights.cost.byTenant.columns.tenant'),
                dataIndex: 'tenant_name',
                render: (name: string | null, r: TenantCostRow) => (
                  <Text style={{ fontSize: 12 }}>
                    {r.unattributed
                      ? t('insights.cost.byTenant.unattributed')
                      : r.tenant_deleted
                        ? t('insights.cost.byTenant.deletedTenant', {
                          id: (r.tenant_id ?? '').slice(0, 8),
                        })
                        : (name ?? '—')}
                  </Text>
                ),
              },
              {
                title: t('insights.cost.byTenant.columns.conversations'),
                dataIndex: 'conversation_count',
                align: 'right' as const,
                width: 110,
                render: fmtN,
              },
              {
                title: t('insights.cost.byTenant.columns.calls'),
                dataIndex: 'call_count',
                align: 'right' as const,
                width: 90,
                render: fmtN,
              },
              {
                title: t('insights.cost.byTenant.columns.input'),
                dataIndex: 'input_cost_usd',
                align: 'right' as const,
                width: 110,
                render: fmtUsd,
              },
              {
                title: t('insights.cost.byTenant.columns.output'),
                dataIndex: 'output_cost_usd',
                align: 'right' as const,
                width: 110,
                render: fmtUsd,
              },
              {
                title: t('insights.cost.byTenant.columns.cacheRead'),
                dataIndex: 'cache_read_cost_usd',
                align: 'right' as const,
                width: 110,
                render: fmtUsd,
              },
              {
                title: t('insights.cost.byTenant.columns.cacheWrite'),
                dataIndex: 'cache_write_cost_usd',
                align: 'right' as const,
                width: 110,
                render: fmtUsd,
              },
              {
                title: t('insights.cost.byTenant.columns.total'),
                dataIndex: 'total_cost_usd',
                align: 'right' as const,
                width: 120,
                defaultSortOrder: 'descend' as const,
                sorter: (a: TenantCostRow, b: TenantCostRow) => a.total_cost_usd - b.total_cost_usd,
                render: (v: number) => <strong>{fmtUsd(v)}</strong>,
              },
              {
                title: t('insights.cost.byTenant.columns.share'),
                key: 'share',
                align: 'right' as const,
                width: 90,
                render: (_: unknown, r: TenantCostRow) => (
                  <Text style={{ fontSize: 12 }}>
                    {tenantCostTotal > 0
                      ? `${((r.total_cost_usd / tenantCostTotal) * 100).toFixed(1)}%`
                      : '—'}
                  </Text>
                ),
              },
            ]}
          />
        </Card>
      )}

      {/* ─── Per-conversation cost scatter ─────────────────────────────────
          Only renders when EXACTLY one agent is selected in the filter
          bar above. Each dot = one conversation; X = its start timestamp,
          Y = its total cost. Lets the user spot expensive outliers,
          long-running conversations, or unusual cost shapes that get
          smoothed away by the per-day rollup above. */}
      <Card
        title={t('insights.costBreakdown.costPerConversationScatter')}
        size="small"
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            {(filters.chatbotIds?.length ?? 0) === 1
              ? t('counts.conversations', { count: convCostPoints.length })
              : t('insights.costBreakdown.pickOneAgent')}
          </Text>
        }
      >
        {(filters.chatbotIds?.length ?? 0) !== 1 ? (
          <Empty
            description={
              <span style={{ fontSize: 13 }}>{t('insights.costBreakdown.scatterNeedsOneAgent')}</span>
            }
            style={{ padding: 24 }}
          />
        ) : convCostPoints.length === 0 ? (
          <Empty description={t('insights.costBreakdown.noConversationsInWindow')} style={{ padding: 24 }} />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={260}>
              {/* Bumped left margin + explicit YAxis width so the currency
                  labels on the left aren't cut off by the card edge. */}
              <ScatterChart margin={{ top: 12, right: 16, bottom: 8, left: 16 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  dataKey="ts"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(t) => dayjs(t).format(datePattern('monthDay'))}
                  tick={{ fontSize: 11 }}
                  name="time"
                />
                <YAxis
                  type="number"
                  dataKey="cost"
                  width={72}
                  tickFormatter={(v) => fmtUsd(v as number)}
                  tick={{ fontSize: 11 }}
                  name="cost"
                />
                <RechartsTooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  formatter={(v: number, name: string) =>
                    name === 'cost'
                      ? [fmtUsd(v), 'Cost']
                      : [dayjs(v).format('YYYY-MM-DD HH:mm'), 'Time']
                  }
                  labelFormatter={() => ''}
                />
                <Scatter
                  name="conversation cost"
                  data={convCostPoints.map((p) => ({
                    ts: new Date(p.created_at).getTime(),
                    cost: p.total_cost_usd,
                    id: p.conversation_id_short,
                  }))}
                  fill="#3b82f6"
                  fillOpacity={0.7}
                />
              </ScatterChart>
            </ResponsiveContainer>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBlockStart: 8 }}>
              {t('insights.costBreakdown.scatterAxes')}
            </Text>
          </>
        )}
      </Card>

      {/* Composition + Call-kind side by side.
          Both panels are tenant-scoped. When tenant scope isn't set we
          render the composition pie (which works platform-wide) but
          show a soft empty state for By Call Kind explaining the
          requirement. */}
      <Row gutter={16}>
        <Col xs={24} lg={10}>
          <Card title={t('insights.costBreakdown.costComposition')}>
            {pieData.length === 0 ? (
              <Empty
                description={t('insights.costBreakdown.noCostData')}
                style={{ padding: 24 }}
              />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={90}
                    innerRadius={50}
                    label={(d) => `${d.name}: ${fmtUsd(d.value as number)}`}
                  >
                    {pieData.map((d, i) => (
                      <Cell key={i} fill={d.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(v: number) => fmtUsd(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card
            title={t('insights.costBreakdown.byCallKind')}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {tenantId
                  ? (chatbotId ? t('insights.costBreakdown.scopedTenantAgent') : t('insights.costBreakdown.scopedTenant'))
                  : t('insights.costBreakdown.pickTenant')}
              </Text>
            }
          >
            {!tenantId ? (
              <Empty
                description={
                  <span>
                    {t('insights.costBreakdown.byCallKindEmpty')}
                  </span>
                }
                style={{ padding: 24 }}
              />
            ) : callKindData.length === 0 ? (
              <Empty
                description={t('insights.costBreakdown.noCallsRecorded')}
                style={{ padding: 24 }}
              />
            ) : (
              <Table<CallKindRow>
                rowKey="call_kind"
                dataSource={callKindData}
                columns={callKindColumns}
                pagination={false}
                size="small"
                /* Each call_kind expands to show the per-tool sub-rows that
                   produced it — e.g. chat -> (main loop) + knowledge_search +
                   web_search etc. Lets the user see exactly which tool
                   inside each kind cost what. */
                expandable={{
                  rowExpandable: (row) => Array.isArray(row.tools) && row.tools.length > 0,
                  expandedRowRender: (row) => (
                    <Table
                      size="small"
                      pagination={false}
                      rowKey="tool_name"
                      dataSource={row.tools}
                      showHeader={false}
                      columns={[
                        {
                          title: t('insights.tool'),
                          dataIndex: 'tool_name',
                          render: (n: string) => (
                            <Text style={{ fontSize: 12, paddingInlineStart: 24 }}>
                              <span style={{ color: '#94a3b8', marginInlineEnd: 6 }}>└─</span>
                              {n}
                            </Text>
                          ),
                        },
                        {
                          title: t('insights.agentStats.cost'),
                          dataIndex: 'total_cost_usd',
                          align: 'right',
                          render: (v: number) => <Text style={{ fontSize: 12 }}>{fmtUsd(v)}</Text>,
                        },
                        {
                          title: 'In',
                          dataIndex: 'input_tokens',
                          align: 'right',
                          render: (v: number) => <Text style={{ fontSize: 12 }}>{fmtN(v)}</Text>,
                        },
                        {
                          title: t('insights.out'),
                          dataIndex: 'output_tokens',
                          align: 'right',
                          render: (v: number) => <Text style={{ fontSize: 12 }}>{fmtN(v)}</Text>,
                        },
                        {
                          title: t('agentDebug.calls'),
                          dataIndex: 'call_count',
                          align: 'right',
                          render: (v: number) => <Text style={{ fontSize: 12 }}>{fmtN(v)}</Text>,
                        },
                      ]}
                    />
                  ),
                }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* By tool — render only when scoped to a tenant. */}
      <Card
        title={t('insights.costBreakdown.byAdkTool')}
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            {!tenantId
              ? t('insights.costBreakdown.pickTenant')
              : chatbotId
                ? t('insights.costBreakdown.scopedToAgent')
                : t('insights.costBreakdown.acrossAllAgents')}
          </Text>
        }
      >
        {!tenantId ? (
          <Empty
            description={
              <span>{t('insights.costBreakdown.byToolEmpty')}</span>
            }
            style={{ padding: 24 }}
          />
        ) : toolData.length === 0 ? (
          <Empty
            description={t('insights.costBreakdown.noToolCalls')}
            style={{ padding: 24 }}
          />
        ) : (
          <Table<ToolRow>
            rowKey="tool_name"
            dataSource={toolData}
            columns={toolColumns}
            pagination={{ pageSize: 10 }}
            size="small"
          />
        )}
      </Card>

      {/* Artifacts — decks / docs / pages / forms agents produced, by kind.
          Artifacts make NO LLM call of their own (the doc model is emitted
          inside the chat turn), so they have no call_kind slice; cost here is
          ATTRIBUTED — the bill of the chat turns that built or edited them,
          linked artifact -> producing message -> llm_call_events. */}
      <Card
        title={t('insights.costBreakdown.artifacts')}
        extra={
          artifactTotals.artifacts > 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {fmtN(artifactTotals.artifacts)} created · {fmtN(artifactTotals.edits)} edits ·{' '}
              {fmtUsd(artifactTotals.cost)} attributed
            </Text>
          ) : null
        }
      >
        {artifactData.length === 0 ? (
          <Empty
            description={t('insights.costBreakdown.noArtifacts')}
            style={{ padding: 24 }}
          />
        ) : (
          <>
            <Table<ArtifactFormatRow>
              rowKey="format"
              dataSource={artifactData}
              columns={artifactColumns}
              pagination={false}
              size="small"
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
              {t('insights.costBreakdown.artifactCostNote')}
            </Text>
          </>
        )}
      </Card>

      {/* Per-conversation timeline — answers "why did THIS turn cost what it cost?" */}
      <Card
        title={t('insights.costBreakdown.perConversationTimeline')}
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('insights.pickAConversationToSee')}
          </Text>
        }
      >
        <ConversationCostTimeline
          tenantId={tenantId}
          chatbotIds={chatbotIds}
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
        />
      </Card>

      {/* Provider rollup (invoice-shape) — one row per provider, aggregated
          across the active filter (window + tenant + agent). Embeddings
          (text-embedding-*) are split off into their own row because
          they're billed under OpenAI but used by every provider's agents
          (RAG retrieval) — so grouping them with OpenAI's chat-model
          spend was misleading. No expandable detail rows: the table is
          a clean summary of "what each provider costs you right now". */}
      <Card
        title={t('insights.costBreakdown.providerRollup')}
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('insights.oneRowPerProviderAggregated')}
          </Text>
        }
      >
        {(() => {
          // Bucket key: either the provider name, OR a synthetic
          // "openai (embeddings)" bucket for text-embedding-* models.
          const bucketFor = (r: ProviderRollupRow) =>
            r.model_name.startsWith('text-embedding')
              ? 'openai (embeddings)'
              : r.provider;

          interface ModelAgg {
            model: string;
            inputTokens: number; readTokens: number; writeTokens: number; outputTokens: number;
            input: number; output: number; read: number; write: number; total: number;
          }
          const byBucket = new Map<string, {
            bucket: string;
            input: number; output: number; read: number; write: number; total: number;
            models: Set<string>;
            // Per-model aggregate across the window — the invoice-shaped
            // model × token-type matrix rendered in the expanded row, for
            // 1:1 comparison with the provider console's cost export.
            modelRows: Map<string, ModelAgg>;
          }>();
          for (const r of providerData) {
            const key = bucketFor(r);
            const e = byBucket.get(key) ?? {
              bucket: key,
              input: 0, output: 0, read: 0, write: 0, total: 0,
              models: new Set<string>(),
              modelRows: new Map<string, ModelAgg>(),
            };
            e.input += r.input_cost_usd;
            e.output += r.output_cost_usd;
            e.read += r.cache_read_cost_usd;
            e.write += r.cache_write_cost_usd;
            e.total += r.total_cost_usd;
            e.models.add(r.model_name);
            const m = e.modelRows.get(r.model_name) ?? {
              model: r.model_name,
              inputTokens: 0, readTokens: 0, writeTokens: 0, outputTokens: 0,
              input: 0, output: 0, read: 0, write: 0, total: 0,
            };
            m.inputTokens += r.input_tokens;
            m.readTokens += r.cache_read_tokens;
            m.writeTokens += r.cache_creation_tokens;
            m.outputTokens += r.output_tokens;
            m.input += r.input_cost_usd;
            m.output += r.output_cost_usd;
            m.read += r.cache_read_cost_usd;
            m.write += r.cache_write_cost_usd;
            m.total += r.total_cost_usd;
            e.modelRows.set(r.model_name, m);
            byBucket.set(key, e);
          }
          const summedRows = Array.from(byBucket.values())
            .sort((a, b) => b.total - a.total);
          const grand = summedRows.reduce(
            (a, b) => ({
              input: a.input + b.input, output: a.output + b.output,
              read: a.read + b.read, write: a.write + b.write, total: a.total + b.total,
            }),
            { input: 0, output: 0, read: 0, write: 0, total: 0 },
          );
          return (
            <>
            <Table
              rowKey={(r: any) => r.bucket}
              dataSource={summedRows as any}
              size="small"
              pagination={false}
              expandable={{
                rowExpandable: (r: any) => r.modelRows?.size > 0,
                expandedRowRender: (r: any) => (
                  <Table<ModelAgg>
                    size="small"
                    pagination={false}
                    rowKey="model"
                    dataSource={Array.from((r.modelRows as Map<string, ModelAgg>).values())
                      .sort((a, b) => b.total - a.total)}
                    columns={[
                      {
                        title: t('insights.cost.providerRollup.columns.model'),
                        dataIndex: 'model',
                        render: (m: string) => (
                          <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{m}</Text>
                        ),
                      },
                      { title: t('insights.cost.providerRollup.columns.inputTokens'),
                        dataIndex: 'inputTokens', align: 'right' as const, render: fmtN },
                      { title: t('insights.cost.providerRollup.columns.cacheReadTokens'),
                        dataIndex: 'readTokens', align: 'right' as const, render: fmtN },
                      { title: t('insights.cost.providerRollup.columns.cacheWriteTokens'),
                        dataIndex: 'writeTokens', align: 'right' as const, render: fmtN },
                      { title: t('insights.cost.providerRollup.columns.outputTokens'),
                        dataIndex: 'outputTokens', align: 'right' as const, render: fmtN },
                      { title: t('insights.cost.byTenant.columns.input'),
                        dataIndex: 'input', align: 'right' as const, render: fmtUsd },
                      { title: t('insights.cost.byTenant.columns.output'),
                        dataIndex: 'output', align: 'right' as const, render: fmtUsd },
                      { title: t('insights.cost.byTenant.columns.cacheRead'),
                        dataIndex: 'read', align: 'right' as const, render: fmtUsd },
                      { title: t('insights.cost.byTenant.columns.cacheWrite'),
                        dataIndex: 'write', align: 'right' as const, render: fmtUsd },
                      { title: t('insights.cost.providerRollup.columns.total'),
                        dataIndex: 'total', align: 'right' as const,
                        render: (v: number) => <strong>{fmtUsd(v)}</strong> },
                    ]}
                  />
                ),
              }}
              columns={[
                { title: t('bots.modelCapabilities.provider'), dataIndex: 'bucket',
                  render: (p: string) => <Tag>{p}</Tag>, width: 180 },
                { title: t('insights.models'),
                  render: (_: any, r: any) => (
                    <Space wrap size={4}>
                      {Array.from(r.models).sort().map((m: any) => (
                        <Tag key={m as string} style={{ fontSize: 10, margin: 0 }}>{m as string}</Tag>
                      ))}
                    </Space>
                  ),
                },
                { title: t('insights.management.input'),       dataIndex: 'input',  align: 'right' as const, render: fmtUsd, width: 110 },
                { title: t('insights.costTimeline.output'),      dataIndex: 'output', align: 'right' as const, render: fmtUsd, width: 110 },
                { title: t('insights.messageBreakdown.cacheRead'),  dataIndex: 'read',   align: 'right' as const, render: fmtUsd, width: 110 },
                { title: t('insights.cost.byApiKey.columns.cacheWrite'), dataIndex: 'write',  align: 'right' as const, render: fmtUsd, width: 110 },
                { title: t('insights.costBreakdown.total'),       dataIndex: 'total',  align: 'right' as const,
                  render: (v: number) => <strong>{fmtUsd(v)}</strong>, width: 120,
                  defaultSortOrder: 'descend' as const,
                  sorter: (a: any, b: any) => a.total - b.total },
              ]}
              summary={() => (
                <Table.Summary.Row>
                  {/* colSpan 3, not 2: the expandable config adds a caret
                      column ahead of Provider, shifting every data column
                      one slot to the right. */}
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong>{t('insights.costBreakdown.total')}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <strong>{fmtUsd(grand.input)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <strong>{fmtUsd(grand.output)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <strong>{fmtUsd(grand.read)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <strong>{fmtUsd(grand.write)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    <strong>{fmtUsd(grand.total)}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBlockStart: 8 }}>
              {t('insights.cost.providerRollup.expandHint')}
            </Text>
            </>
          );
        })()}
      </Card>
    </div>
  );
}
